import React, { useState, useEffect, useCallback, useRef } from "react";
import Creatable from "react-select/creatable";
import TableInputs from "../../components/tableinputs/TableInputs";
import Buttons from "../../components/buttons/Buttons";
import Customstyles from "../../pages/joinourteam/JoinOurTeam.module.css";
import { useNavigate, useLocation } from "react-router-dom";
import { AxiosInstance } from "../../axios";
import { Modal, Button } from "react-bootstrap";
import PreviewBlog from "../previewblog/PreviewBlog";
import CustomQuillEditor from "../../CustomQuillEditor ";
import ModalComponent from "../../components/modalcomponent/ModalComponent";

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

// Helper function to strip HTML tags and check for meaningful content
const isContentEmpty = (htmlString) => {
  if (typeof htmlString !== "string" || !htmlString) {
    return true;
  }

  const emptyHtmlPatterns = [
    "<p></p>",
    "<p><br></p>",
    "<p>&nbsp;</p>",
    "<div></div>",
    "<div><br></div>",
    "<div>&nbsp;</div>",
    "<br>",
    "<br/>",
    "&nbsp;",
    '<p class="ql-align-left"></p>',
    '<p class="ql-align-center"></p>',
    '<p class="ql-align-right"></p>',
    '<p class="ql-align-justify"></p>',
    "&lt;p&gt;&lt;br&gt;&lt;/p&gt;",
  ];

  const trimmedHtmlString = htmlString.trim();
  const isRawHtmlEffectivelyEmpty = emptyHtmlPatterns.some(
    (pattern) => trimmedHtmlString === pattern,
  );

  const strippedText = trimmedHtmlString.replace(/<[^>]*>/g, "");
  const finalTrimmedText = strippedText.replace(/&nbsp;|\u200B/g, "").trim();

  return isRawHtmlEffectivelyEmpty || finalTrimmedText.length === 0;
};

// Helper: validate YouTube URL (accepts watch, share, embed/iframe, shorts, m., live)
const isValidYoutubeUrl = (url) => {
  if (!url) return false;
  const youtubeRegex =
    /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/|v\/|live\/)|youtu\.be\/)[\w-]{6,}(\S*)?$/i;
  return youtubeRegex.test(url.trim());
};

function CreateBlog() {
  const navigate = useNavigate();
  const location = useLocation();
  const { blogId, blogData } = location.state || {};
  const [contentSize, setContentSize] = useState(0);

  const fileInputRef = useRef(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const skipValidationRef = useRef(false);

  const initialFormData = {
    title: "",
    tagline: "",
    meta_title: "",
    meta_description: "",
    content: "",
    author_name: "",
    author_designation: "",
    categories: [],
    tag: [],
    cover_image_originalname: "",
    cover_image_path: "",
    youtube_url: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [open, setOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState({ title: "", body: "" });
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalBlogDetails, setOriginalBlogDetails] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [shouldNavigateAfterClose, setShouldNavigateAfterClose] =
    useState(false);

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Fetch categories and tags on component mount
  useEffect(() => {
    AxiosInstance.get("/api/blog/categorieslist")
      .then((res) => {
        if (res.data.status) {
          setCategoryOptions(
            res.data.tag.map((cat) => ({
              label: cat.name,
              value: cat.id,
            })),
          );
        }
      })
      .catch((err) => console.error("Error fetching categories:", err));

    AxiosInstance.get("/api/blog/taglist")
      .then((res) => {
        if (res.data.status) {
          setTagOptions(
            res.data.tag.map((tag) => ({
              label: tag.name,
              value: tag.id,
            })),
          );
        }
      })
      .catch((err) => console.error("Error fetching tags:", err));
  }, []);

  const MAX_CONTENT_SIZE = 15 * 1024 * 1024; // 15MB safe limit

  const getContentSize = (content) => {
    return new Blob([content || ""]).size;
  };
  useEffect(() => {
    if (blogId && blogData) {
      setIsEditMode(true);
      setOriginalBlogDetails(blogData);

      const fetchedCategories = [];
      if (blogData.categories_id) {
        if (categoryOptions.length > 0) {
          const existingCat = categoryOptions.find(
            (opt) => String(opt.value) === String(blogData.categories_id),
          );
          if (existingCat) {
            fetchedCategories.push({
              categories_id: String(existingCat.value),
              categories_name: existingCat.label,
            });
          } else {
            fetchedCategories.push({
              categories_id: String(blogData.categories_id) || "",
              categories_name: blogData.categorie_name || "",
            });
          }
        } else {
          // If categoryOptions haven't loaded yet, still populate with data
          fetchedCategories.push({
            categories_id: String(blogData.categories_id) || "",
            categories_name: blogData.categorie_name || "",
          });
        }
      }

      const fetchedTags = [];
      if (
        blogData.tag &&
        Array.isArray(blogData.tag) &&
        tagOptions.length > 0
      ) {
        blogData.tag.forEach((tagItem) => {
          const existingTag = tagOptions.find(
            (opt) => String(opt.value) === String(tagItem.tag_id),
          );
          if (existingTag) {
            fetchedTags.push({
              tag_id: String(existingTag.value),
              tag_name: existingTag.label,
            });
          } else {
            fetchedTags.push({
              tag_id: String(tagItem.tag_id) || "",
              tag_name: tagItem.tag_name,
            });
          }
        });
      }

      setFormData({
        title: blogData.title || "",
        tagline: blogData.tagline || "",
        meta_title: blogData.meta_title || "",
        meta_description: blogData.meta_description || "",
        content: blogData.content || "",
        author_name: blogData.author_name || "",
        author_designation: blogData.author_designation || "",
        categories: fetchedCategories,
        tag: fetchedTags,
        cover_image_originalname: blogData.cover_image_originalname || "",
        cover_image_path: blogData.cover_image_path || "",
        youtube_url: blogData.youtube_url || "",
      });

      if (blogData.cover_image_path) {
        setPreviewUrl(blogData.cover_image_path);
      }
      setFileInputKey((prevKey) => prevKey + 1);
    } else if (!blogId) {
      skipValidationRef.current = true;
      setFormData(initialFormData);
      setErrors({});
      setOriginalBlogDetails(null);
      setPreviewUrl(null);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setFileInputKey((prevKey) => prevKey + 1);

      setTimeout(() => {
        skipValidationRef.current = false;
      }, 0);
    }
  }, [blogId, blogData, categoryOptions, tagOptions, navigate]);

  const validateField = useCallback((name, value) => {
    // Regex to detect emojis
    const emojiRegex =
      /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2190-\u2BFF]|\u24C2|\uFE0F)/g;

    // Check for emojis in all text fields
    if (
      [
        "title",
        "tagline",
        "meta_title",
        "meta_description",
        "author_name",
        "author_designation",
        "youtube_url",
      ].includes(name) &&
      typeof value === "string" &&
      emojiRegex.test(value)
    ) {
      return "Emojis are not allowed.";
    }

    // Check for emojis in categories
    if (name === "categories" && value && value.length > 0) {
      for (const cat of value) {
        if (cat.categories_name && emojiRegex.test(cat.categories_name)) {
          return `emojis are not allowed.`;
        }
      }
    }

    // Check for emojis in tags
    if (name === "tag" && value && value.length > 0) {
      for (const tag of value) {
        if (tag.tag_name && emojiRegex.test(tag.tag_name)) {
          return `emojis are not allowed.`;
        }
      }
    }

    if (name === "title" && !value) return "Title is required.";
    if (name === "tagline" && !value) return "Tagline is required.";

    if (name === "meta_title") {
      if (!value) return "Meta title is required.";
      if (value.length > 60) return "Meta title must not exceed 60 characters.";
    }
    if (name === "meta_description") {
      if (!value) return "Meta description is required.";
      if (value.length > 160)
        return "Meta description must not exceed 160 characters.";
    }

    if (name === "content") {
      if (isContentEmpty(value)) {
        return "Content is required.";
      }
    }
    if (name === "author_name" && !value) return "Author name is required.";
    if (name === "author_designation" && !value)
      return "Author designation is required.";
    if (name === "categories" && (!value || value.length === 0))
      return "Category is required.";
    if (name === "tag" && (!value || value.length === 0))
      return "At least one tag is required.";

    // YouTube URL is OPTIONAL - only validate format when a value is provided.
    if (name === "youtube_url" && value && value.trim() !== "") {
      if (!isValidYoutubeUrl(value)) {
        return "Please enter a valid YouTube URL.";
      }
    }

    return "";
  }, []);

  // NOTE: meta_title (60) and meta_description (160) are NO LONGER hard-capped
  // here. The user can type freely past the limit; once they cross it, the
  // existing length check inside validateField returns the appropriate error
  // message which is then rendered under the field. Submission is still
  // blocked while that error exists because handleSubmit re-runs validateField
  // on every field before posting.
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (!skipValidationRef.current) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // YouTube URL is optional - this handler only validates its own field.
  // It no longer touches the cover-image error because the two fields are
  // independent (image is mandatory, YouTube is optional, both may coexist).
  const handleYoutubeUrlChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (!skipValidationRef.current) {
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleTextEditorChange = (htmlContent) => {
    const cleanContent = htmlContent === "<p><br></p>" ? "" : htmlContent;

    const size = getContentSize(cleanContent);

    setContentSize(size);

    if (size > MAX_CONTENT_SIZE) {
      setErrors((prev) => ({
        ...prev,
        content:
          "Content size exceeds 15MB limit. Please reduce images/videos.",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      content: cleanContent,
    }));

    if (!skipValidationRef.current) {
      const error = validateField("content", cleanContent);
      setErrors((prev) => ({
        ...prev,
        content: error,
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setPreviewUrl(null);
      setSelectedFile(null);
      setFormData((prev) => ({
        ...prev,
        cover_image_originalname: "",
        cover_image_path: "",
      }));
      // Cover image is MANDATORY - show required error whenever no image is
      // present (regardless of whether a YouTube URL has been entered).
      if (
        !isEditMode ||
        (isEditMode &&
          !formData.cover_image_path &&
          !originalBlogDetails?.cover_image_path)
      ) {
        setErrors((prev) => ({
          ...prev,
          cover_image_originalname: "Cover image is required.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, cover_image_originalname: "" }));
      }
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2MB

    if (allowedTypes.includes(file.type)) {
      // Size validation - on FAILURE, KEEP the existing/previous image intact.
      // We only reset the native <input> value so the user can re-pick a file.
      // Do NOT touch selectedFile, previewUrl, or formData here, otherwise the
      // existing cover image (in edit mode) or the previously-selected valid
      // image (in create mode) would be wiped out, which is exactly the bug
      // the user reported.
      if (file.size > MAX_IMAGE_SIZE) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setErrors((prev) => ({
          ...prev,
          cover_image_originalname: "Image size must be less than 2MB.",
        }));
        return;
      }

      setPreviewUrl(URL.createObjectURL(file));
      setSelectedFile(file);
      setErrors((prev) => ({ ...prev, cover_image_originalname: "" }));
    } else {
      // Invalid file type - same principle as the >2MB case: keep the existing
      // image intact, only reset the native input so the user can try again.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      if (!skipValidationRef.current) {
        setErrors((prev) => ({
          ...prev,
          cover_image_originalname: "Only JPEG, PNG, JPG images are allowed.",
        }));
      } else {
        setErrors((prev) => ({ ...prev, cover_image_originalname: "" }));
      }
    }
  };

  const uploadImage = async (file) => {
    const formDataImage = new FormData();
    formDataImage.append("upload_image", file);

    try {
      const res = await AxiosInstance.post(
        "/api/joinourteam/upload",
        formDataImage,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data.status && res.data.file && res.data.file.upload_image) {
        return {
          originalname: res.data.file.original_image_path,
          path: res.data.file.upload_image,
        };
      } else {
        throw new Error(
          res.data.message ||
            "Image upload failed: Invalid response structure.",
        );
      }
    } catch (err) {
      console.error("Image upload error:", err);
      setModalMessage({
        title: "Error",
        body: err.message || "Failed to upload image.",
      });
      setOpen(true);
      return null;
    }
  };

  // Image and YouTube are now INDEPENDENT - both may be filled together.
  // Image is mandatory; YouTube is optional. Neither field disables the other.
  const isImageDisabled = false;
  const isYoutubeDisabled = false;

  const handleSubmit = async (e) => {
    e.preventDefault();

    skipValidationRef.current = false;
    const contentSize = new Blob([formData.content]).size;

    if (contentSize > MAX_CONTENT_SIZE) {
      setModalMessage({
        title: "Content Too Large",
        body: "Blog content exceeds the 16MB limit. Please reduce images or videos.",
      });
      setOpen(true);
      return;
    }
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      // Skip cover_image_path / cover_image_originalname / youtube_url here;
      // image is validated as mandatory below, youtube_url is optional and
      // is format-validated separately when provided.
      if (
        key === "cover_image_path" ||
        key === "cover_image_originalname" ||
        key === "youtube_url"
      ) {
        return;
      }
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });

    // YouTube URL format validation (optional - only check when provided)
    if (formData.youtube_url && formData.youtube_url.trim() !== "") {
      const ytError = validateField("youtube_url", formData.youtube_url);
      if (ytError) {
        newErrors.youtube_url = ytError;
      }
    }

    // Cover image is MANDATORY (YouTube URL no longer satisfies this requirement).
    const imageProvided =
      !!selectedFile ||
      (isEditMode &&
        !!originalBlogDetails?.cover_image_path &&
        !!formData.cover_image_path) ||
      !!formData.cover_image_path;

    if (!imageProvided) {
      newErrors.cover_image_originalname = "Cover image is required.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"], .ql-editor`,
      );
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    const user_type = getCookie("user_type") || 1;
    const created_by = getCookie("user_id") || "";

    let finalCoverImageOriginalname = formData.cover_image_originalname;
    let finalCoverImagePath = formData.cover_image_path;

    if (selectedFile) {
      const uploadedImageData = await uploadImage(selectedFile);
      if (!uploadedImageData) {
        return;
      }
      finalCoverImageOriginalname = uploadedImageData.originalname;
      finalCoverImagePath = uploadedImageData.path;
    } else if (
      isEditMode &&
      !selectedFile &&
      originalBlogDetails?.cover_image_path
    ) {
      // Edit mode + no new file selected: always retain the original image
      // (regardless of whether a YouTube URL is also present - both coexist).
      finalCoverImageOriginalname =
        originalBlogDetails.cover_image_originalname;
      finalCoverImagePath = originalBlogDetails.cover_image_path;
    }

    const basePayload = {
      user_type: parseInt(user_type),
      title: formData.title,
      tagline: formData.tagline,
      meta_title: formData.meta_title,
      meta_description: formData.meta_description,
      content: formData.content,
      author_name: formData.author_name,
      author_designation: formData.author_designation,
      categories_id:
        formData.categories.length > 0
          ? formData.categories[0].categories_id || ""
          : "",
      categories_name:
        formData.categories.length > 0
          ? formData.categories[0].categories_name || ""
          : "",
      tag: formData.tag.map((t) => ({
        tag_id: t.tag_id || "",
        tag_name: t.tag_name,
      })),
      cover_image_originalname: finalCoverImageOriginalname,
      cover_image_path: finalCoverImagePath,
      // YouTube URL is optional - send the trimmed value, or an empty string
      // when not provided.
      youtube_url: formData.youtube_url ? formData.youtube_url.trim() : "",
    };
    let apiEndpoint = "";
    let payload = {};

    if (isEditMode && blogId) {
      payload = {
        ...basePayload,
        id: blogId,
        updated_by: created_by,
        blog_status: 1,
        deleted: 0,
      };
      apiEndpoint = "/api/blog/update";
    } else {
      payload = {
        ...basePayload,
        created_by: created_by,
        blog_status: 1,
        created_date: new Date().toISOString().slice(0, 10),
      };
      apiEndpoint = "/api/blog/create";
    }

    try {
      const res = await AxiosInstance.post(apiEndpoint, payload);
      setModalMessage({
        title: res.data.status ? "Success" : "Failed",
        body: res.data.status
          ? isEditMode
            ? "The blog details have been updated successfully"
            : "The new blog post has been created successfully and is now visible on the user side."
          : res.data.message || "Something went wrong",
      });
      setOpen(true);

      if (res.data.status) {
        setShouldNavigateAfterClose(true);

        skipValidationRef.current = true;
        setFormData(initialFormData);
        setErrors({});
        setPreviewUrl(null);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setFileInputKey((prevKey) => prevKey + 1);
        setIsEditMode(false);
      }
    } catch (err) {
      console.error("API submission error:", err);
      setModalMessage({
        title: "Error",
        body: err.message || "Failed to submit form.",
      });
      setOpen(true);
    }
  };

  const handleClearForm = () => {
    skipValidationRef.current = true;
    setFormData(initialFormData);
    setErrors({});
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setFileInputKey((prevKey) => prevKey + 1);
    setIsEditMode(false);

    setTimeout(() => {
      skipValidationRef.current = false;
      if (blogId) {
        navigate("/admin/create-blog", { replace: true });
      }
    }, 0);
  };

  const handleModalClose = () => {
    setOpen(false);
    if (shouldNavigateAfterClose) {
      navigate("/admin/Blog");
      setShouldNavigateAfterClose(false);
    }
  };

  const handlePreview = () => {
    skipValidationRef.current = false;

    const newErrors = {};
    const fieldsToValidateForPreview = [
      "title",
      "tagline",
      "meta_title",
      "meta_description",
      "content",
      "author_name",
      "author_designation",
      "categories",
      "tag",
    ];
    fieldsToValidateForPreview.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    // YouTube URL format check (only if provided - optional)
    if (formData.youtube_url && formData.youtube_url.trim() !== "") {
      const ytError = validateField("youtube_url", formData.youtube_url);
      if (ytError) newErrors.youtube_url = ytError;
    }

    // Cover image is MANDATORY for preview.
    const imageProvided = !!selectedFile || !!formData.cover_image_path;

    if (!imageProvided) {
      newErrors.cover_image_originalname =
        "Cover image is required for preview.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setModalMessage({
        title: "Validation Error",
        body: "Please fill in all required fields and upload a cover image to preview the blog.",
      });
      setOpen(true);
      return;
    }
    setShowPreviewModal(true);
  };

  const previewBlogData = {
    title: formData.title,
    tagline: formData.tagline,
    meta_title: formData.meta_title,
    meta_description: formData.meta_description,
    content: formData.content,
    author_name: formData.author_name,
    author_designation: formData.author_designation,
    category: formData.categories.map((cat) => ({
      category_name: cat.categories_name,
    })),
    tag: formData.tag.map((t) => ({ tag_name: t.tag_name })),
    cover_image_path: selectedFile
      ? previewUrl
      : isEditMode && formData.cover_image_path
        ? formData.cover_image_path
        : "https://placehold.co/100x100?text=No+Image",
    youtube_url: formData.youtube_url || "",
    posted_date: new Date().toISOString(),
  };

  // Filename to display INSIDE the custom file input (replacing the native
  // browser "No file chosen" text - which can't be changed programmatically
  // on a real <input type="file">).
  // - New file picked this session  -> show selectedFile.name
  // - Edit mode, no new pick yet    -> show originally-uploaded filename
  // - Otherwise                     -> fall back to "No file chosen"
  const currentCoverFileName = selectedFile
    ? selectedFile.name
    : isEditMode
      ? formData.cover_image_originalname || ""
      : "";

  // Click handler that forwards a click on our custom "Choose File" button
  // (or the surrounding text area) to the hidden native <input type="file">
  // so the OS file picker opens.
  const triggerHiddenFileInput = () => {
    if (!isImageDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="container-fluid pt-3">
      {/* Modals for success/failure */}
      <ModalComponent
        show={open}
        onHide={handleModalClose}
        title={modalMessage.title}
        bodyText={modalMessage.body}
        buttontext="OK"
      />
      {/* Preview Modal */}
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        fullscreen={true}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Blog Post Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto" }}
        >
          <PreviewBlog blogData={previewBlogData} />
        </Modal.Body>
        <Modal.Footer></Modal.Footer>
      </Modal>
      {/* Main Form */}
      <form onSubmit={handleSubmit}>
        <div className={`card p-4 ${Customstyles.customCard}`}>
          <div className="row">
            {/* Meta Title and Meta Description at the top.
                NOTE: maxLength was intentionally removed from these two fields
                so that the user can keep typing past the 60 / 160 character
                limits. validateField surfaces the over-limit error inline,
                and handleSubmit re-validates before posting, so submission is
                still blocked when over the limit. */}
            <div className="col-md-6 mb-2">
              <TableInputs
                label="Meta Title (Max 60 characters)"
                name="meta_title"
                placeholder="Enter Meta Title"
                value={formData.meta_title}
                onChange={handleChange}
              />
              {errors.meta_title && (
                <span className="custom_error">{errors.meta_title}</span>
              )}
            </div>
            <div className="col-md-6 mb-2">
              <TableInputs
                label="Meta Description (Max 160 characters)"
                name="meta_description"
                placeholder="Enter Meta Description"
                value={formData.meta_description}
                onChange={handleChange}
              />
              {errors.meta_description && (
                <span className="custom_error">{errors.meta_description}</span>
              )}
            </div>

            <div className="col-md-6 mb-2">
              <TableInputs
                label="Title"
                name="title"
                placeholder="Enter blog Title"
                value={formData.title}
                onChange={handleChange}
              />
              {errors.title && (
                <span className="custom_error">{errors.title}</span>
              )}
            </div>
            <div className="col-md-6 mb-2">
              <TableInputs
                label="Tagline"
                name="tagline"
                placeholder="Enter blog Tagline"
                value={formData.tagline}
                onChange={handleChange}
              />
              {errors.tagline && (
                <span className="custom_error">{errors.tagline}</span>
              )}
            </div>
            <div className="col-md-6 mb-2">
              <TableInputs
                label="Author"
                name="author_name"
                placeholder="Enter Author Name"
                value={formData.author_name}
                onChange={handleChange}
              />
              {errors.author_name && (
                <span className="custom_error">{errors.author_name}</span>
              )}
            </div>
            <div className="col-md-6 mb-2">
              <TableInputs
                label="Designation"
                name="author_designation"
                placeholder="Enter Author Designation"
                value={formData.author_designation}
                onChange={handleChange}
              />
              {errors.author_designation && (
                <span className="custom_error">
                  {errors.author_designation}
                </span>
              )}
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label">Category</label>
              <Creatable
                classNamePrefix="select"
                className="basic-single-select"
                name="categories"
                placeholder="Select or enter a category..."
                value={
                  formData.categories.length > 0
                    ? {
                        label: formData.categories[0].categories_name,
                        value:
                          formData.categories[0].categories_id ||
                          formData.categories[0].categories_name,
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  // Emoji check on selected/created category
                  const emojiRegex =
                    /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2190-\u2BFF]|\u24C2|\uFE0F)/g;

                  if (selectedOption && emojiRegex.test(selectedOption.label)) {
                    setErrors((prev) => ({
                      ...prev,
                      categories: `emojis are not allowed.`,
                    }));
                    return; // Stop execution if emoji found
                  }

                  let updatedCategories = [];

                  if (selectedOption) {
                    // Trim the label to remove leading/trailing spaces
                    const trimmedLabel = selectedOption.label.trim();

                    // Check if trimmed label is empty after trimming
                    if (!trimmedLabel) {
                      setErrors((prev) => ({
                        ...prev,
                        categories:
                          "Category name cannot be empty or just spaces.",
                      }));
                      return;
                    }

                    // Check if the trimmed label already exists in categoryOptions (case-insensitive)
                    const existing = categoryOptions.find(
                      (s) =>
                        s.label.toLowerCase().trim() ===
                        trimmedLabel.toLowerCase(),
                    );

                    if (existing) {
                      // Use existing category
                      updatedCategories.push({
                        categories_id: String(existing.value),
                        categories_name: existing.label, // Use the original label from options
                      });
                    } else {
                      // Check if any existing category has the same trimmed name but different case/spacing
                      const similarExisting = categoryOptions.find(
                        (s) =>
                          s.label.toLowerCase().trim() ===
                          trimmedLabel.toLowerCase(),
                      );

                      if (similarExisting) {
                        // If found with different case/spacing, use that instead of creating new
                        updatedCategories.push({
                          categories_id: String(similarExisting.value),
                          categories_name: similarExisting.label,
                        });

                        // Optional: Show a message to user
                        console.log(
                          `Using existing category "${similarExisting.label}" instead of creating new one`,
                        );
                      } else {
                        // Create new category with trimmed name
                        updatedCategories.push({
                          categories_id: "",
                          categories_name: trimmedLabel, // Store trimmed version
                        });
                      }
                    }
                  }

                  setFormData((prev) => ({
                    ...prev,
                    categories: updatedCategories,
                  }));

                  setErrors((prev) => ({ ...prev, categories: "" }));
                }}
                onInputChange={(inputValue, actionMeta) => {
                  const trimmed = inputValue.trimStart();

                  if (actionMeta.action === "input-change") {
                    const emojiRegex =
                      /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2190-\u2BFF]|\u24C2|\uFE0F)/g;

                    if (emojiRegex.test(trimmed)) {
                      setErrors((prev) => ({
                        ...prev,
                        categories: "Emojis are not allowed in category.",
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, categories: "" }));
                    }
                  }

                  return trimmed;
                }}
                options={categoryOptions}
                formatCreateLabel={(inputValue) =>
                  `Add New: "${inputValue.trim()}"`
                }
                isValidNewOption={(inputValue, selectValue, selectOptions) => {
                  const trimmed = inputValue.trim().toLowerCase();

                  if (!trimmed) return false;

                  // Check if trimmed value already exists in options (case-insensitive)
                  const exists = selectOptions.some(
                    (opt) => opt.label.toLowerCase().trim() === trimmed,
                  );

                  return !exists;
                }}
                noOptionsMessage={() => {
                  return "Emojis are not allowed in category";
                }}
                getOptionLabel={(option) => option.label} // Ensure original label is displayed
                getOptionValue={(option) => option.value}
                isClearable={true}
                styles={{
                  control: (base) => ({ ...base, minHeight: 40 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {errors.categories && (
                <span className="custom_error">{errors.categories}</span>
              )}
            </div>
            <div className="col-md-6 mb-2">
              <label className="form-label">Tags</label>
              <Creatable
                isMulti
                classNamePrefix="select"
                className="basic-multi-select"
                name="tag"
                placeholder="Select or enter new tag(s)..."
                value={formData.tag.map((t) => ({
                  label: t.tag_name,
                  value: t.tag_id || t.tag_name,
                }))}
                onChange={(selectedOptions) => {
                  // Check for emojis in all tags (both existing and new)
                  const emojiRegex =
                    /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2190-\u2BFF]|\u24C2|\uFE0F)/g;

                  if (selectedOptions && selectedOptions.length > 0) {
                    for (const opt of selectedOptions) {
                      if (emojiRegex.test(opt.label)) {
                        setErrors((prev) => ({
                          ...prev,
                          tag: `emojis are not allowed.`,
                        }));
                        return; // Stop execution if emoji found
                      }
                    }
                  }

                  const updatedTags = selectedOptions.map((opt) => {
                    const trimmedLabel = opt.label.trim();

                    const existing = tagOptions.find(
                      (s) =>
                        s.label.toLowerCase().trim() ===
                        trimmedLabel.toLowerCase(),
                    );

                    if (existing) {
                      return {
                        tag_id: String(existing.value),
                        tag_name: existing.label,
                      };
                    } else {
                      return {
                        tag_id: "",
                        tag_name: trimmedLabel,
                      };
                    }
                  });

                  setFormData((prev) => ({
                    ...prev,
                    tag: updatedTags,
                  }));

                  // Clear any previous tag errors
                  setErrors((prev) => ({ ...prev, tag: "" }));
                }}
                onInputChange={(inputValue, actionMeta) => {
                  const trimmed = inputValue.trimStart();

                  if (actionMeta.action === "input-change") {
                    const emojiRegex =
                      /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2190-\u2BFF]|\u24C2|\uFE0F)/g;

                    if (emojiRegex.test(trimmed)) {
                      setErrors((prev) => ({
                        ...prev,
                        tag: "Emojis are not allowed in tags.",
                      }));
                    } else {
                      setErrors((prev) => ({ ...prev, tag: "" }));
                    }
                  }

                  return trimmed;
                }}
                options={tagOptions.filter(
                  (opt) =>
                    !formData.tag.some(
                      (t) => String(t.tag_id) === String(opt.value),
                    ),
                )}
                formatCreateLabel={(inputValue) => `Add New: "${inputValue}"`}
                isValidNewOption={(inputValue, selectValue, selectOptions) => {
                  const trimmed = inputValue.trim().toLowerCase();

                  if (!trimmed) return false;

                  const exists = selectOptions.some(
                    (opt) => opt.label.toLowerCase().trim() === trimmed,
                  );

                  return !exists;
                }}
                noOptionsMessage={() => {
                  return "Emojis are not allowed in tags";
                }}
                isClearable={false}
                styles={{
                  control: (base) => ({ ...base, minHeight: 40 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {errors.tag && <span className="custom_error">{errors.tag}</span>}
            </div>

            {/* Cover Image (col-6) | YouTube URL (col-6) - independent.
                Image is mandatory; YouTube is optional. Both may be filled.

                NOTE on the cover-image field:
                The native <input type="file"> hard-codes "No file chosen" /
                "<filename>" and that text cannot be programmatically changed.
                To show the currently-attached filename (especially the
                originally-uploaded one in edit mode) INSIDE the input box,
                we hide the real file input and render a Bootstrap-styled
                facade - a "Choose File" button + a text area - and forward
                clicks to the hidden input. The hidden input still does all
                the real work (file selection, onChange, accept filter). */}
            <div className="col-md-6 mb-2">
              <label className="form-label" htmlFor="cover_image_file">
                Upload Cover Image (JPG, JPEG, PNG | Max size: 2MB)
              </label>

              <div
                className="form-control p-0 d-flex align-items-stretch"
                style={{
                  height: "auto",
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <button
                  type="button"
                  onClick={triggerHiddenFileInput}
                  disabled={isImageDisabled}
                  style={{
                    background: "#e9e9ed",
                    border: "none",
                    borderRight: "1px solid #ced4da",
                    padding: "6px 14px",
                    cursor: isImageDisabled ? "not-allowed" : "pointer",
                    flexShrink: 0,
                    color: "#000",
                  }}
                >
                  Choose File
                </button>
                <span
                  onClick={triggerHiddenFileInput}
                  title={currentCoverFileName || "No file chosen"}
                  style={{
                    flex: 1,
                    padding: "6px 12px",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    display: "flex",
                    alignItems: "center",
                    cursor: isImageDisabled ? "not-allowed" : "pointer",
                    color: currentCoverFileName ? "#212529" : "#6c757d",
                  }}
                >
                  {currentCoverFileName || "No file chosen"}
                </span>
              </div>

              {/* Hidden real file input - does the actual work */}
              <input
                ref={fileInputRef}
                key={fileInputKey}
                type="file"
                name="cover_image_file"
                id="cover_image_file"
                accept=".jpeg,.png,.jpg"
                onChange={handleImageChange}
                disabled={isImageDisabled}
                style={{ display: "none" }}
              />

              {errors.cover_image_originalname && (
                <span className="custom_error">
                  {errors.cover_image_originalname}
                </span>
              )}

              {(previewUrl || formData.cover_image_path) && (
                <div
                  style={{
                    width: "100px",
                    height: "100px",
                    marginTop: "10px",
                    border: "1px solid #ccc",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={
                      previewUrl ||
                      (isEditMode && formData.cover_image_path
                        ? formData.cover_image_path
                        : "https://placehold.co/100x100?text=No+Image")
                    }
                    alt="Cover Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="col-md-6 mb-2">
              <TableInputs
                label="YouTube URL (Optional)"
                name="youtube_url"
                placeholder="Paste a YouTube link (watch, share, embed, or shorts)"
                value={formData.youtube_url}
                onChange={handleYoutubeUrlChange}
                disabled={isYoutubeDisabled}
              />
              {errors.youtube_url && (
                <span className="custom_error">{errors.youtube_url}</span>
              )}
            </div>

            <div className="col-md-12 mb-2">
              <label className="form-label">Content</label>
              <CustomQuillEditor
                value={formData.content}
                onChange={handleTextEditorChange}
              />
              {errors.content && (
                <span className="custom_error">{errors.content}</span>
              )}
            </div>
            <div className="row" style={{ marginTop: "90px" }}>
              <div className="col-md-4 d-flex justify-content-end">
                <Buttons
                  type="button"
                  text="Cancel"
                  onClick={handleClearForm}
                  variant="secondary"
                />
              </div>
              <div className="col-md-4 d-flex justify-content-center ">
                <Buttons
                  type="button"
                  text="Preview"
                  onClick={handlePreview}
                  variant="info"
                />
              </div>
              <div className="col-md-4 d-flex justify-content-start">
                <Buttons
                  type="submit"
                  text={isEditMode ? "Update" : "Submit"}
                />
              </div>
            </div>
          </div>
        </div>
      </form>
      <div style={{ fontSize: "12px", marginTop: "5px" }}>
        Content Size: {((contentSize || 0) / (1024 * 1024)).toFixed(2)} MB / 15
        MB
      </div>
    </div>
  );
}

export default CreateBlog;
